import React, { useState, useEffect, useRef } from 'react';
import axiosInstance from '../../../helpers/axios';
import EventIcon from '@mui/icons-material/Event';
import GppBadRoundedIcon from '@mui/icons-material/GppBadRounded';
import AccountBalanceWalletRoundedIcon from '@mui/icons-material/AccountBalanceWalletRounded';
import SavingsRoundedIcon from '@mui/icons-material/SavingsRounded';
import TaskAltRoundedIcon from '@mui/icons-material/TaskAltRounded';

function buildBucketRows(bucketPerfData) {
  if (!bucketPerfData?.bucketPerformance) return [];
  return bucketPerfData.bucketPerformance.map((b) => ({
    bucketName: b.bucket,
    loansCount: b.totalLoans || 0,
    overdueAmount: b.overdueAmount || 0,
    collectedAmount: b.collectedAmount || 0,
    collectionPct: b.collectionPct || 0,
    target: b.target || 'Per File',
    gap: b.gap,
    status: b.status,
  }));
}

export default function ManagerOverview({ initialData }) {
  const [dashboard, setDashboard]   = useState(initialData?.dashboardRaw ?? null);
  const [bucketRows, setBucketRows] = useState(initialData?.bucketPerfRaw ? buildBucketRows(initialData.bucketPerfRaw) : []);
  const [loading, setLoading]       = useState(!initialData);

  const [searchQuery, setSearchQuery]     = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (initialData) return;
    Promise.all([
      axiosInstance.get('/collection-incentive/manager/dashboard'),
      axiosInstance.get('/collection-incentive/manager/buckets'),
    ])
      .then(([dashRes, bucketRes]) => {
        setDashboard(dashRes.data.data);
        setBucketRows(buildBucketRows(bucketRes.data.data));
      })
      .catch((error) => {
        alert(error?.response?.data?.message || 'Failed to load overview');
      })
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync when parent refreshes while this tab is mounted
  useEffect(() => {
    if (!initialData) return;
    setDashboard(initialData.dashboardRaw);
    setBucketRows(buildBucketRows(initialData.bucketPerfRaw));
    setLoading(false);
  }, [initialData]);

  const handleForceRotation = () => {
    if (!window.confirm('Are you sure you want to force a rotation now?')) return;
    axiosInstance
      .post('/collection-incentive/manager/force-rotation', {})
      .then(() => alert('Rotation triggered successfully'))
      .catch((error) => alert(error?.response?.data?.message || 'Failed to trigger rotation'));
  };

  const handleSearchChange = (value) => {
    setSearchQuery(value);
    clearTimeout(debounceRef.current);
    if (value.trim().length < 2) { setSearchResults([]); return; }
    debounceRef.current = setTimeout(() => {
      setSearchLoading(true);
      axiosInstance.get('/collection-incentive/manager/search', { params: { q: value.trim() } })
        .then((res) => setSearchResults(res.data.data || []))
        .catch(() => setSearchResults([]))
        .finally(() => setSearchLoading(false));
    }, 300);
  };

  if (loading) return <div style={{ padding: '20px', color: '#667085' }}>Loading...</div>;
  if (!dashboard) return <div style={{ padding: '20px', color: '#667085' }}>No data available.</div>;

  const { rotation, stats, teamSummary } = dashboard;

  // Parse day-of-week from the rotation date string for the banner sub-label
  const rotDay = (() => {
    if (!rotation?.nextRotationDate) return null;
    const d = new Date(rotation.nextRotationDate);
    return isNaN(d.getTime()) ? null : d.toLocaleDateString('en-US', { weekday: 'long' });
  })();

  // Derive totals from analytics bucket data (same source as the Bucket Performance table below)
  const totalOverdueLoans = bucketRows.reduce((sum, b) => sum + b.loansCount, 0);
  const totalOverdueAmt = bucketRows.reduce((sum, b) => sum + b.overdueAmount, 0);
  const totalCollectedAmt = bucketRows.reduce((sum, b) => sum + b.collectedAmount, 0);
  // "Pending Collection" = total overdue minus what's already collected this month
  const pendingAmt = totalOverdueAmt;

  const fmt = (n) => `₹${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

  const statCards = [
    {
      label:    'Total Overdue Loans',
      value:    totalOverdueLoans || '—',
      icon:     <GppBadRoundedIcon style={{ fontSize: '24px', color: '#d32028' }} />,
      iconBg:   '#fff0f0',
      cardBg:   'linear-gradient(to bottom left, #ffb3b3 0%, #ffffff 60%)',
      border:   '#f0f0f0',
    },
    {
      label:    'Pending Collection',
      value:    pendingAmt > 0 ? fmt(pendingAmt) : '—',
      icon:     <AccountBalanceWalletRoundedIcon style={{ fontSize: '24px', color: '#f59e0b' }} />,
      iconBg:   '#fffbeb',
      cardBg:   'linear-gradient(to bottom left, #fdd888 0%, #ffffff 60%)',
      border:   '#f0f0f0',
    },
    {
      label:    'Total Recovered',
      value:    stats?.totalRecovered != null ? fmt(stats.totalRecovered) : '—',
      icon:     <SavingsRoundedIcon style={{ fontSize: '24px', color: '#16a34a' }} />,
      iconBg:   '#f0fdf4',
      cardBg:   'linear-gradient(to bottom left, #86efac 0%, #ffffff 60%)',
      border:   '#f0f0f0',
    },
    {
      label:    'Active PTPs',
      value:    stats?.activePtps ?? 0,
      icon:     <TaskAltRoundedIcon style={{ fontSize: '24px', color: '#2563eb' }} />,
      iconBg:   '#eff6ff',
      cardBg:   'linear-gradient(to bottom left, #93c5fd 0%, #ffffff 60%)',
      border:   '#f0f0f0',
    },
  ];

  return (
    <div style={{ padding: '4px 0' }}>

      {/* Rotation Banner */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        backgroundColor: '#fff5f5', border: '1px solid #fee2e2',
        borderRadius: '12px', padding: '6px 14px', marginBottom: '10px',
        position: 'relative', overflow: 'hidden',
      }}>
        {/* Decorative background circles */}
        {/* Decorative background circles — right side only */}
        <div style={{ position: 'absolute', right: '160px', top: '-35px', width: '130px', height: '130px', borderRadius: '50%', backgroundColor: 'rgba(211,32,40,0.05)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', right: '100px', top: '10px', width: '80px', height: '80px', borderRadius: '50%', backgroundColor: 'rgba(211,32,40,0.04)', pointerEvents: 'none' }} />

        <div style={{ display: 'flex', alignItems: 'center', gap: '20px', zIndex: 1 }}>
          {/* Big calendar icon */}
          <div style={{
            width: '34px', height: '34px', borderRadius: '50%', flexShrink: 0,
            backgroundColor: 'white', boxShadow: '0 2px 8px rgba(211,32,40,0.12)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <EventIcon style={{ fontSize: '17px', color: '#d32028' }} />
          </div>

          {/* Next Rotation */}
          <div style={{ paddingRight: '24px', borderRight: '1px solid #fecaca' }}>
            <p style={{ margin: 0, fontSize: '11px', color: '#9e9e9e', fontFamily: 'Inter-Medium' }}>Next Rotation</p>
            <p style={{ margin: '1px 0 0', fontSize: '14px', fontWeight: '700', color: '#101828', fontFamily: 'Inter-Medium' }}>
              {rotation?.nextRotationDate || 'Not scheduled'}
            </p>
            {rotDay && (
              <p style={{ margin: '0', fontSize: '10px', color: '#9e9e9e', fontFamily: 'Inter-Medium' }}>{rotDay}</p>
            )}
          </div>

          {/* PTP Held Count */}
          <div style={{ paddingLeft: '4px', paddingRight: '24px', borderRight: '1px solid #fecaca' }}>
            <p style={{ margin: 0, fontSize: '11px', color: '#9e9e9e', fontFamily: 'Inter-Medium' }}>PTP Held Count</p>
            <p style={{ margin: '1px 0 0', fontSize: '14px', fontWeight: '700', color: '#101828', fontFamily: 'Inter-Medium' }}>
              {rotation?.ptpHeldCount ?? 0}
            </p>
          </div>

          {/* Total Agents */}
          <div style={{ paddingLeft: '4px' }}>
            <p style={{ margin: 0, fontSize: '11px', color: '#9e9e9e', fontFamily: 'Inter-Medium' }}>Total Agents</p>
            <p style={{ margin: '1px 0 0', fontSize: '14px', fontWeight: '700', color: '#101828', fontFamily: 'Inter-Medium' }}>
              {rotation?.totalAgents ?? 0}
            </p>
          </div>
        </div>

        <button
          onClick={handleForceRotation}
          style={{
            display: 'flex', alignItems: 'center', gap: '7px', zIndex: 1,
            padding: '8px 18px', backgroundColor: '#d32028', color: 'white',
            border: 'none', borderRadius: '8px', fontFamily: 'Inter-Medium',
            fontSize: '13px', fontWeight: '600', cursor: 'pointer', flexShrink: 0,
          }}
          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#b71c1c'; }}
          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#d32028'; }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="white">
            <path d="M12 4V1L8 5l4 4V6c3.31 0 6 2.69 6 6 0 1.01-.25 1.97-.7 2.8l1.46 1.46C19.54 15.03 20 13.57 20 12c0-4.42-3.58-8-8-8zm0 14c-3.31 0-6-2.69-6-6 0-1.01.25-1.97.7-2.8L5.24 7.74C4.46 8.97 4 10.43 4 12c0 4.42 3.58 8 8 8v3l4-4-4-4v3z" />
          </svg>
          Force Rotation
        </button>
      </div>

      {/* 4 Stat Cards */}
      <div style={{ display: 'flex', gap: '14px', marginBottom: '20px' }}>
        {statCards.map((card) => (
          <div
            key={card.label}
            style={{
              flex: 1,
              background: card.cardBg,
              border: `1px solid ${card.border}`, borderRadius: '12px',
              padding: '14px 16px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px',
            }}
          >
            <div>
              <p style={{ margin: 0, fontSize: '12px', color: '#667085', fontFamily: 'Inter-Medium', marginBottom: '4px' }}>
                {card.label}
              </p>
              <p style={{ margin: 0, fontSize: '20px', fontWeight: '700', color: '#344054', fontFamily: 'Inter-Medium' }}>
                {card.value}
              </p>
            </div>
            <div style={{
              width: '44px', height: '44px', borderRadius: '10px', flexShrink: 0,
              backgroundColor: card.iconBg,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              {card.icon}
            </div>
          </div>
        ))}
      </div>

      {/* Loan Search */}
      <div style={{ marginBottom: '24px' }}>
        <p style={{ fontFamily: 'Inter-Medium', fontSize: '15px', color: '#344054', marginBottom: '10px', fontWeight: '600' }}>
          Loan Search
        </p>
        <div style={{ position: 'relative' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '8px 12px', backgroundColor: '#fff' }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9e9e9e" strokeWidth="2">
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder="Search by loan ID, borrower name, or phone..."
              style={{ flex: 1, border: 'none', outline: 'none', fontSize: '13px', fontFamily: 'Inter-Medium', color: '#344054', backgroundColor: 'transparent' }}
            />
            {searchLoading && (
              <span style={{ fontSize: '11px', color: '#9e9e9e' }}>Searching...</span>
            )}
            {searchQuery && (
              <span onClick={() => { setSearchQuery(''); setSearchResults([]); }} style={{ cursor: 'pointer', color: '#9e9e9e', fontSize: '16px', lineHeight: 1 }}>×</span>
            )}
          </div>

          {searchQuery.trim().length >= 2 && (
            <div style={{ marginTop: '8px' }}>
              {searchResults.length === 0 && !searchLoading ? (
                <div style={{ padding: '12px 14px', border: '1px solid #e0e0e0', borderRadius: '8px', fontSize: '12px', color: '#9e9e9e', fontFamily: 'Inter-Medium' }}>
                  No results found
                </div>
              ) : (
                searchResults.map((r) => (
                  <div key={r.loanId} style={{ padding: '10px 14px', border: '1px solid #e0e0e0', borderRadius: '8px', marginBottom: '6px', backgroundColor: '#fafbfc', fontFamily: 'Inter-Medium' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '13px', fontWeight: '600', color: '#1565c0' }}>{r.legacyLoanId || r.loanId}</span>
                        <span style={{ fontSize: '12px', color: '#344054' }}>— {r.borrowerName}</span>
                      </div>
                      <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '600', backgroundColor: '#e3f2fd', color: '#1565c0' }}>
                        {r.bucket}
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: '16px', fontSize: '12px', color: '#667085' }}>
                      <span>Agent: <strong style={{ color: '#344054' }}>{r.agentName || '—'}</strong></span>
                      <span>DPD: <strong style={{ color: '#344054' }}>{r.dpd}</strong></span>
                      <span>Overdue: <strong style={{ color: '#d32028' }}>₹{Number(r.overdue).toLocaleString('en-IN')}</strong></span>
                      <span>Phone: <strong style={{ color: '#344054' }}>{r.phone}</strong></span>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>

      {/* Team Summary Table */}
      <div style={{ marginBottom: '32px' }}>
        <p style={{ fontFamily: 'Inter-Medium', fontSize: '15px', color: '#344054', marginBottom: '10px', fontWeight: '600' }}>
          Team Summary
        </p>
        <div style={{ overflowX: 'auto', border: '1px solid #e0e0e0', borderRadius: '8px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'Inter-Medium', fontSize: '13px' }}>
            <thead>
              <tr style={{ backgroundColor: '#f9fafb' }}>
                {['Agent Name', 'Loans Assigned', 'Recovered (₹)', 'PTP Count', 'Incentive Earned (₹)'].map((col) => (
                  <th key={col} style={{ padding: '4px 8px', textAlign: 'left', color: '#667085', fontWeight: '600', borderBottom: '1px solid #e0e0e0', whiteSpace: 'nowrap' }}>
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {teamSummary && teamSummary.length > 0 ? (
                teamSummary.map((agent, index) => (
                  <tr key={agent.agentId || index} style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '4px 8px', color: '#344054', fontWeight: '500' }}>{agent.agentName || '—'}</td>
                    <td style={{ padding: '4px 8px', color: '#344054' }}>{agent.loansAssigned ?? '—'}</td>
                    <td style={{ padding: '4px 8px', color: '#344054' }}>
                      {agent.recovered != null ? `₹${Number(agent.recovered).toLocaleString('en-IN')}` : '—'}
                    </td>
                    <td style={{ padding: '4px 8px', color: '#344054' }}>{agent.ptpCount ?? '—'}</td>
                    <td style={{ padding: '4px 8px', color: '#344054' }}>
                      {agent.incentiveEarned != null ? `₹${Number(agent.incentiveEarned).toLocaleString('en-IN')}` : '—'}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} style={{ padding: '20px', textAlign: 'center', color: '#667085' }}>
                    No agents assigned yet. Use the Allocation tab to assign loans to agents.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Bucket Performance Table — sourced from /analytics/collection-performance */}
      <div>
        <p style={{ fontFamily: 'Inter-Medium', fontSize: '15px', color: '#344054', marginBottom: '10px', fontWeight: '600' }}>
          Bucket Performance
        </p>
        <div style={{ overflowX: 'auto', border: '1px solid #e0e0e0', borderRadius: '8px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'Inter-Medium', fontSize: '13px' }}>
            <thead>
              <tr style={{ backgroundColor: '#f9fafb' }}>
                {['Bucket', 'Loans', 'Remaining (₹)', 'Collected (₹)', 'Collection %', 'Target', 'Gap (₹)', 'Status'].map((col) => (
                  <th key={col} style={{ padding: '4px 8px', textAlign: 'left', color: '#667085', fontWeight: '600', borderBottom: '1px solid #e0e0e0', whiteSpace: 'nowrap' }}>
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {bucketRows.length > 0 ? (
                bucketRows.map((bucket, index) => (
                  <tr key={bucket.bucketName || index} style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '4px 8px', color: '#344054', fontWeight: '500' }}>{bucket.bucketName || '—'}</td>
                    <td style={{ padding: '4px 8px', color: '#344054' }}>{bucket.loansCount ?? '—'}</td>
                    <td style={{ padding: '4px 8px', color: '#344054' }}>
                      {`₹${Number(bucket.overdueAmount).toLocaleString('en-IN')}`}
                    </td>
                    <td style={{ padding: '4px 8px', color: '#344054' }}>
                      {`₹${Number(bucket.collectedAmount).toLocaleString('en-IN')}`}
                    </td>
                    <td style={{ padding: '4px 8px', color: '#344054' }}>{bucket.collectionPct}%</td>
                    <td style={{ padding: '4px 8px', color: '#344054' }}>{bucket.target}</td>
                    <td style={{ padding: '4px 8px', color: '#344054' }}>
                      {bucket.gap != null ? `₹${Number(bucket.gap).toLocaleString('en-IN')}` : '—'}
                    </td>
                    <td style={{ padding: '4px 8px' }}>
                      {bucket.status ? (
                        <span style={{
                          padding: '3px 10px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: '600',
                          backgroundColor: bucket.status === 'On Track' ? '#e6f4ea' : '#fdecea',
                          color: bucket.status === 'On Track' ? '#2e7d32' : '#c62828',
                        }}>
                          {bucket.status}
                        </span>
                      ) : '—'}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={8} style={{ padding: '20px', textAlign: 'center', color: '#667085' }}>No overdue bucket data found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

    </div>
  );
}
